import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import EditableCell from './EditableCell';
import DateDayNavigator from './DateDayNavigator';

export default function PrioritiesTab({ api, user, selectedBase, setAlerts, globalView }) {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const todayLocal = `${yyyy}-${mm}-${dd}`;

  const [selectedDate, setSelectedDate] = useState(todayLocal);
  const [records, setRecords] = useState([]);
  const [personnelOptions, setPersonnelOptions] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [canDeletePriorities, setCanDeletePriorities] = useState(false);
  const dropdownRefs = useRef({});

  const isCarryForward = (rec) => rec.date < selectedDate;

  // Fetch current priorities and personnel
  const fetchPriorities = async () => {
    try {
      const res = await api.get('/get-priorities', {
        params: { 
          email: user.email, 
          location: selectedBase, 
          date: selectedDate, 
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          global: user.is_superuser && globalView ? true : undefined }
      });
      // update records and personnel options
      setRecords(
        res.data.data.map(r => ({
          ...r,
          id: String(r.id),
          personnel: Array.isArray(r.personnel) ? r.personnel : []
        }))
      );
      setPersonnelOptions(
        (res.data.personnel || []).map(p => {
          const label = `${p['Last Name']}, ${p['First Name']} (${p['Shop Name']})`;
          const value = String(p['Emp Num'] || '').trim();
          const isPresent = (res.data.present || []).some(pr => pr['Emp Num'] === p['Emp Num']);
          return {
            value,
            label,
            isPresent,
            disabled: !value,
            disabledReason: !value ? 'Employee number required' : ''
          };
        })
      );
      setCanDeletePriorities(Boolean(res.data.canDeletePriorities));
    } catch (err) {
      console.error('Error fetching priorities:', err);
    }
  };

  useEffect(() => {
    if (user && selectedBase) fetchPriorities();
  }, [user, selectedBase, selectedDate]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = e => {
      if (
        !e.target.closest('.personnel-dropdown') &&
        !Object.values(dropdownRefs.current).some(el => el?.contains(e.target))
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Swap priorities and persist
  const changePriority = async (rec, delta) => {
    const sectionRecs = records
      .filter(r => r.section === rec.section)
      .sort((a, b) => a.priority - b.priority);
    const idx = sectionRecs.findIndex(r => r.id === rec.id);
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= sectionRecs.length) return;
    const other = sectionRecs[newIdx];

    // Optimistic swap in UI
    setRecords(prev => prev.map(r => {
      if (r.id === rec.id)      return { ...r, priority: other.priority };
      if (r.id === other.id)    return { ...r, priority: rec.priority };
      return r;
    }));

    try {
      // update backend including priority field
      await api.post('/update-priority', {
        ...rec,
        priority: other.priority,
        email: user.email,
        location: selectedBase
      });
      await api.post('/update-priority', {
        ...other,
        priority: rec.priority,
        email: user.email,
        location: selectedBase
      });
      // re-fetch to ensure sync
      await fetchPriorities();
    } catch (err) {
      console.error('Error swapping priorities:', err);
    }
  };

  // Save or add record and refresh
  const saveRecord = async rec => {
    const payload = { ...rec, email: user.email, location: selectedBase };
    try {
      if (!rec.id.startsWith('new-')) {
        await api.post('/update-priority', payload);
      } else {
        const res = await api.post('/add-priority', payload);
        if (res.data.id) rec.id = String(res.data.id);
      }
      if (payload.complied) {
        setAlerts(prev => [
          { id: Date.now(), action: 'PRIORITY_COMPLIED', message: `[${rec.tail}] priority ${rec.priority} complied` },
          ...prev
        ]);
      }
      await fetchPriorities();
    } catch (err) {
      console.error('Error saving record:', err);
    }
  };

  // Add new row
  const handleAddRow = section => {
    const newRec = {
      section,
      date: selectedDate,
      priority: records.filter(x => x.section === section).length + 1,
      tail: '',
      discrepancy: '',
      personnel: [],
      complied: false,
      id: `new-${Date.now()}`
    };
    setRecords(prev => [...prev, newRec]);
  };

  const deleteRecord = async rec => {
    if (!rec.id.startsWith('new-') && !canDeletePriorities) {
      window.alert('Only Commander, Production, siteAdmin, Management, Pro-Super, Expediter, or a superuser may delete priorities.');
      return;
    }

    const label = rec.tail || rec.discrepancy || `priority ${rec.priority}`;
    if (!rec.id.startsWith('new-') && !window.confirm(`Remove ${label} from ${rec.section}?`)) {
      return;
    }

    if (rec.id.startsWith('new-')) {
      setRecords(prev => prev.filter(item => item.id !== rec.id));
      return;
    }

    try {
      const res = await api.post('/delete-priority', {
        id: rec.id,
        email: user.email,
        location: selectedBase,
      });
      const returnedAlert = res.data.alert;
      setAlerts(prev => [
        {
          id: returnedAlert?.id || Date.now(),
          tail: returnedAlert?.tail || rec.tail || rec.section,
          action: returnedAlert?.action || 'PRIORITY_DELETED',
          details: returnedAlert?.details || '',
          message: returnedAlert?.message || `[${rec.tail || rec.section}] priority ${rec.priority} removed`,
        },
        ...prev
      ]);
      await fetchPriorities();
    } catch (err) {
      console.error('Error deleting priority:', err);
      window.alert(err.response?.data?.message || 'Unable to remove priority.');
    }
  };

  const sections = ['General', 'Crew Chiefs', 'Avionics', 'Electrics', 'Engines', 'Hydro'];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <DateDayNavigator id="prioritiesDate" value={selectedDate} onChange={setSelectedDate} />
      </div>
      <p style={{ margin: '0 0 12px', color: '#53667c', fontSize: '0.9rem' }}>
        C/W Y marks this team's Priority assignment complete. When the overall task is complete, record its actual dates and status in Status.
      </p>

      {sections.map(sec => {
        const sectionRecords = records.filter(r => r.section === sec).sort((a, b) => a.priority - b.priority);
        return (
          <div key={sec} style={{ marginBottom: 24 }}>
            <h3>{sec}</h3>
            <div style={{ overflowX: 'auto', position: 'relative' }}>
              <table className="use-colgroup">  
                <colgroup>
                   <col style={{ width: '80px' }} />
                   <col style={{ width: '80px' }} />
                   <col style={{ minWidth: '200px' }} />
                   <col />
                   <col style={{ width: '60px' }} />
                   <col style={{ width: '90px' }} />
                 </colgroup>
                <thead>
                  <tr>
                    <th>Priority</th>
                    <th>Tail #</th>
                    <th>Discrepancy</th>
                    <th>Personnel Assigned</th>
                    <th>C/W</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sectionRecords.map(rec => (
                    <tr 
                      key={rec.id}
                      style={{
                        backgroundColor: rec.date < selectedDate ? '#f9f9f9' : 'white',
                        fontStyle: rec.date < selectedDate ? 'italic' : 'normal'
                      }}
                    >    
                      <td style={{ textAlign: 'center' }}>
                        <button disabled={rec.priority === 1} onClick={() => changePriority(rec, -1)}>▲</button>
                        <span style={{ margin: '0 4px' }}>{rec.priority}</span>
                        <button onClick={() => changePriority(rec, 1)}>▼</button>
                      </td>
                      <td>
                        <EditableCell
                          value={rec.tail}
                          onSave={v => saveRecord({ ...rec, tail: v })}
                          emptyLabel="Add tail"
                        />
                        {isCarryForward(rec) && (
                          <div style={{ fontSize: 12, color: '#888' }}>
                            (carryforward from {new Date(rec.date).toLocaleDateString()})
                          </div>
                        )}
                      </td>
                      <td>
                        <EditableCell
                          value={rec.discrepancy}
                          onSave={v => saveRecord({ ...rec, discrepancy: v })}
                          emptyLabel="Add discrepancy"
                        />
                      </td>
                      <td className="personnel-dropdown">
                        <div
                          ref={el => (dropdownRefs.current[rec.id] = el)}
                          onClick={e => { e.stopPropagation(); setOpenDropdown(openDropdown === rec.id ? null : rec.id); }}
                          className={rec.personnel.length ? 'priority-personnel-picker' : 'priority-personnel-picker priority-personnel-picker--empty'}
                          style={{ border: '1px solid #ccc', padding: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          {rec.personnel.length
                            ? rec.personnel
                                .map(id => personnelOptions.find(o => o.value === id)?.label)
                                .filter(Boolean)
                                .join(', ')
                            : 'Select personnel'}
                        </div>
                        {openDropdown === rec.id &&
                          dropdownRefs.current[rec.id] &&
                          ReactDOM.createPortal(
                            <div
                              className="personnel-dropdown"
                              onClick={e => e.stopPropagation()}
                              style={{
                                position: 'absolute',
                                top: dropdownRefs.current[rec.id].getBoundingClientRect().bottom +
                                  window.scrollY,
                                left: dropdownRefs.current[rec.id].getBoundingClientRect().left +
                                  window.scrollX,
                                background: '#fff',
                                border: '1px solid #ccc',
                                zIndex: 10000,
                                maxHeight: '200px',
                                overflowY: 'auto',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {personnelOptions.map(opt => (
                                <label 
                                  key={opt.value} 
                                  style={{ 
                                    display: 'block', 
                                    padding: '4px', 
                                    color: opt.disabled ? '#7a3e00' : '#173047',
                                    cursor: opt.disabled ? 'not-allowed' : 'pointer'
                                  }}
                                  title={opt.disabledReason || (opt.isPresent ? 'Present' : 'Not marked present')}
                                >
                                  <input
                                    type="checkbox"
                                    checked={rec.personnel.includes(opt.value)}
                                    disabled={opt.disabled}
                                    onChange={e => {
                                      e.stopPropagation();
                                      const newArr = e.target.checked
                                        ? [...rec.personnel, opt.value]
                                        : rec.personnel.filter(v => v !== opt.value);
                                      saveRecord({ ...rec, personnel: newArr });
                                    }}
                                  />{' '}
                                  {opt.label}
                                  {!opt.isPresent && !opt.disabled ? ' — Not marked present' : ''}
                                  {opt.disabled ? ` — ${opt.disabledReason}` : ''}
                                </label>
                              ))}
                            </div>,
                            document.body
                          )}
                      </td>
                      <td>
                        <select
                          value={rec.complied ? 'Y' : 'N'}
                          onChange={e => saveRecord({ ...rec, complied: e.target.value === 'Y' })}
                          title="Complied with for this Priority assignment only"
                        >
                          <option value="N">--</option>
                          <option value="Y">Y</option>
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="priority-delete-button"
                          onClick={() => deleteRecord(rec)}
                          disabled={!rec.id.startsWith('new-') && !canDeletePriorities}
                          title={canDeletePriorities || rec.id.startsWith('new-') ? 'Remove this priority' : 'Only Commander, Production, siteAdmin, Management, Pro-Super, Expediter, or a superuser may delete priorities'}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={() => handleAddRow(sec)}>+ Add Row</button>
          </div>
        );
      })}
    </div>
  );
}
